import React, { useState, useEffect, useRef } from 'react';
import { cn } from '@/utils/cn';
import ButtonLabel from '../Typography/ButtonLabel';
import TextSmall from '../Typography/TextSmall';

function CaptureImage({ isCameraOn, setIsCameraOn, onView, onClose }) {
  const [openModal, setOpenModal] = useState(onView);
  const [isScreenSmall, setIsScreenSmall] = useState(false);
  const modalRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [capturedImage, setCapturedImage] = useState(null);

  const handleClose = () => {
    setOpenModal(false);
    setIsCameraOn(false);

    // Stop all camera tracks safely
    const video = videoRef.current;
    if (video && video.srcObject) {
      const stream = video.srcObject;
      const tracks = stream.getTracks();
      tracks.forEach((track) => track.stop());
      video.srcObject = null; // clear reference
    }

    setTimeout(() => {
      onClose(false);
    }, 200);
  };

  const handleCapture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);

    const imageDataUrl = canvas.toDataURL('image/png');
    setCapturedImage(imageDataUrl);

    // Optional: Convert to Blob/File for upload
    // canvas.toBlob((blob) => { ...upload logic... }, 'image/png');
  };

  useEffect(() => {
    if (onView) {
      setTimeout(() => {
        setOpenModal(true);
        document.body.style.overflow = 'hidden';
      }, 100);
    } else {
      setOpenModal(false);
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [onView]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        handleClose();
      }
    };

    if (openModal) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [openModal]);

  useEffect(() => {
    if (isCameraOn && videoRef.current) {
      navigator.mediaDevices
        .getUserMedia({ video: true })
        .then((stream) => {
          videoRef.current.srcObject = stream;
        })
        .catch((err) => {
          console.error('Error accessing camera:', err);
        });
    }
  }, [isCameraOn]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const handleResize = () => {
        setIsScreenSmall(window.innerWidth < 1024);
      };
      handleResize();

      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  return (
    <div
      className={cn(
        'w-full h-full bg-black/90 flex justify-center items-center opacity-30 transition-all duration-100 fixed top-full left-0 right-0',
        {
          '!top-0 !opacity-100 !z-[501]': openModal,
        },
      )}
    >
      <div
        className={cn(
          `w-full h-full flex items-center justify-center rounded-[4px] sm:rounded-[12px] transition-all duration-[400ms]`,
          {
            '!bottom-0 !opacity-100': openModal,
          },
        )}
      >
        <div
          className="relative bg-white p-6 rounded-lg flex flex-col items-center gap-4"
          ref={modalRef}
        >
          <video
            ref={videoRef}
            autoPlay
            playsInline
            className="rounded-lg border w-full max-w-md"
          >
            <track
              kind="captions"
              src="captions.vtt"
              srcLang="en"
              label="English"
              default
            />
          </video>
          <button
            type="button"
            onClick={handleCapture}
            className="px-4 py-2 bg-blue-600 text-white rounded-md"
          >
            <ButtonLabel label="Capture" className="text-base" />
          </button>
          {capturedImage && (
            <div className="mt-4">
              <TextSmall
                text="Captured Image:"
                className="text-sm lg:text-sm text-center"
              />
              <img
                src={capturedImage}
                alt="Captured"
                className="mt-2 rounded-md max-w-xs border"
              />
            </div>
          )}
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>
      </div>
    </div>
  );
}

export default CaptureImage;
